import { useQuery } from '@apollo/client'
import { SearchProductCard } from '@dfh-corp-sites/ui'
import React, { useContext, useMemo } from 'react'

import { MenuEntryContentFragment } from '../../../../graphql/gatsby'
import { ParentProductsBySkusDocument } from '../../../../graphql/search'
import useLogAndCaptureError from '../../../../hooks/useLogAndCaptureError'
import { useRefetchOnChange } from '../../../../hooks/useRefetchOnChange'
import { TokenContext } from '../../../../layouts/context'
import { handleProductTagCategoryPriority } from '../../../../lib/products/tags'
import { getResizedImageUrl, tryParsingSanityImageJson } from '../../../../lib/sanity/utils'
import { isNotNull } from '../../../../utils/collectionTools'
import { imageUrlFor } from '../../../../utils/image-url'
import Spinner from '../../../Spinner'
import Link from '../../Link'

export interface MenuEntryProductsProps {
  sanityProducts: NonNullable<MenuEntryContentFragment['products']>['products']
}

export const MenuEntryProducts: React.FC<MenuEntryProductsProps> = ({ sanityProducts }) => {
  const token = useContext(TokenContext)
  const skus = sanityProducts?.map((product) => product?.sku).filter(isNotNull) || []
  const { data, loading, error, refetch, networkStatus } = useQuery(ParentProductsBySkusDocument, {
    context: {
      token,
      uri: process.env.GATSBY_SEARCH_URL,
    },
    variables: {
      skus: skus,
      first: 10,
    },
    notifyOnNetworkStatusChange: true,
    skip: !skus || sanityProducts?.length === 0,
  })
  useRefetchOnChange({ networkStatus, refetch }, [token])
  useLogAndCaptureError(error)

  const products = data?.parentProducts.nodes

  const processedProducts = useMemo(() => {
    if (!products) return []

    return products.map((product) => {
      let processedImageUrl = ''
      let srcSet = ''

      if (product.mainImage?.json) {
        const sanityImage = tryParsingSanityImageJson(product.mainImage.json)
        if (sanityImage) {
          // Use 2x image as default
          processedImageUrl =
            getResizedImageUrl(sanityImage, { width: 169, dpr: 2, fit: 'max' }) || ''

          // Generate srcset for responsive images
          const imageBuilder = imageUrlFor(sanityImage).auto('format').fit('max')
          srcSet = [
            `${imageBuilder.width(169).url()} 169w`,
            `${imageBuilder.width(338).dpr(2).url()} 338w`,
          ].join(', ')
        }
      }

      return {
        ...product,
        processedImageUrl,
        srcSet,
        processedUrl: `/products/${product.urlKey}`,
      }
    })
  }, [products])

  if (loading) {
    return <Spinner loading />
  }

  if (!processedProducts.length) return null

  return (
    <>
      {processedProducts.map((product) => (
        <SearchProductCard
          tag={handleProductTagCategoryPriority(product.tagCategories)}
          key={product.sku}
          title={<Link to={product.processedUrl}>{product.name}</Link>}
          image={{
            src: product.processedImageUrl ?? '',
            srcSet: product.srcSet || undefined,
            sizes: '169px',
            alt: product.name,
            loading: 'eager' as const,
          }}
        />
      ))}
    </>
  )
}
